import React, { useState } from 'react';
import { GroupMemberProfile, Match, CardCosmeticTheme } from '../types';
import { FootballCard } from './FootballCard';
import { CommunityScoutingModal } from './CommunityScoutingModal';
import { ALL_ACHIEVEMENTS } from '../data/achievements';
import { calculateAllPositionsOVR } from '../services/ratingCalculator';
import { X, Award, Trophy, Star, Shield, Goal, ArrowRightLeft, Sparkles, CheckCircle2, ChevronRight, Share2, Calendar } from 'lucide-react';

interface PlayerProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  member: GroupMemberProfile;
  allMembers: GroupMemberProfile[];
  matches: Match[];
  currentUserId: string;
  isOrganizer: boolean;
  onOpenEditProfile?: () => void;
  onOpenManageRating?: (memberId: string) => void;
  onOpenCompare?: (memberId: string) => void;
  onSubmitCommunityRating?: (targetMemberId: string, rating: any) => void;
  onApplyCommunitySuggestion?: (targetMemberId: string, suggestedAttrs: any) => void;
  onUpdateCosmeticTheme?: (memberId: string, theme: CardCosmeticTheme) => void;
}

export const PlayerProfileModal: React.FC<PlayerProfileModalProps> = ({
  isOpen,
  onClose,
  member,
  allMembers,
  matches,
  currentUserId,
  isOrganizer,
  onOpenEditProfile,
  onOpenManageRating,
  onOpenCompare,
  onSubmitCommunityRating,
  onApplyCommunitySuggestion,
  onUpdateCosmeticTheme,
}) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'stats' | 'matches' | 'card' | 'achievements'>('overview');
  const [selectedSeason, setSelectedSeason] = useState<string>('all_time');
  const [isScoutModalOpen, setIsScoutModalOpen] = useState(false);

  const isSelf = member.userId === currentUserId;

  // Calculate position versatility ratings
  const positionRatings = calculateAllPositionsOVR(member.footballCard);

  // Filter player matches
  const playerMatches = matches
    .filter((m) => m.status === 'completed' && m.result?.statsRecorded)
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  // Derive recent match stats and ratings for Current Form
  const playerRecentPerformance = playerMatches
    .map((m) => {
      const pStat = m.result?.playerStats?.find((s) => s.memberProfileId === member.id);
      const isTeamA = m.teamA?.players.some((p) => p.memberProfileId === member.id);
      const isTeamB = m.teamB?.players.some((p) => p.memberProfileId === member.id);

      let outcome: 'W' | 'L' | 'D' = 'D';
      if (m.result) {
        if (m.result.teamAScore > m.result.teamBScore) {
          outcome = isTeamA ? 'W' : (isTeamB ? 'L' : 'D');
        } else if (m.result.teamBScore > m.result.teamAScore) {
          outcome = isTeamB ? 'W' : (isTeamA ? 'L' : 'D');
        }
      }

      return {
        matchId: m.id,
        title: m.title,
        date: m.date,
        rating: pStat?.rating ?? 6.5,
        goals: pStat?.goals ?? 0,
        assists: pStat?.assists ?? 0,
        outcome,
      };
    });

  const last5 = playerRecentPerformance.slice(0, 5);
  const displayFormItems = last5.length > 0 ? last5 : [
    { rating: 8.1, outcome: 'W' as const, goals: 1 },
    { rating: 7.4, outcome: 'W' as const, goals: 0 },
    { rating: 8.8, outcome: 'W' as const, goals: 2 },
    { rating: 6.9, outcome: 'D' as const, goals: 0 },
    { rating: 8.3, outcome: 'W' as const, goals: 1 },
  ];
  const formAvg = (displayFormItems.reduce((a, b) => a + b.rating, 0) / displayFormItems.length).toFixed(1);

  // Unlocked achievements
  const unlockedCodes = member.unlockedAchievements || ['first_goal', 'apps_10'];

  const COSMETIC_OPTIONS: { theme: CardCosmeticTheme; label: string; desc: string }[] = [
    { theme: 'default', label: 'Default Card', desc: 'Standard sleek dark football profile card' },
    { theme: 'centurion_100', label: 'Centurion 100', desc: 'Unlocked after 100 appearances' },
    { theme: 'top_scorer', label: 'Golden Boot', desc: 'Awarded to top goalscorers' },
    { theme: 'motm_king', label: 'MOTM Star', desc: 'Awarded to frequent Man of the Match winners' },
    { theme: 'tournament_champ', label: 'Champion', desc: 'Tournament winner edition' },
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4">
      <div className="bg-[#121614] border border-white/10 rounded-2xl w-full max-w-2xl p-4 sm:p-6 shadow-2xl overflow-y-auto max-h-[92vh] text-white">
        
        {/* Modal Top Bar */}
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-slate-400">Player Profile</span>
            <span className="text-[10px] font-mono text-[#a8c936] bg-[#a8c936]/10 px-2 py-0.5 rounded border border-[#a8c936]/20">
              #{member.jerseyNumber}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {onOpenCompare && (
              <button
                onClick={() => onOpenCompare(member.id)}
                className="flex items-center gap-1 px-2.5 py-1 bg-white/5 hover:bg-white/10 text-xs font-semibold text-slate-300 rounded-lg border border-white/5 transition"
              >
                <ArrowRightLeft className="w-3.5 h-3.5 text-[#a8c936]" />
                <span className="hidden sm:inline">Compare</span>
              </button>
            )}
            <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-white/5 transition">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Profile Header Banner */}
        <div className="bg-[#171c19] border border-white/5 rounded-2xl p-4 mb-5 relative overflow-hidden">
          <div className="flex items-center gap-4">
            <img
              src={member.avatar || 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=200'}
              alt={member.displayName}
              className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl object-cover border-2 border-white/10 shrink-0"
            />

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-xl sm:text-2xl font-bold text-white truncate">{member.displayName}</h2>
                <span className="text-xs font-bold text-[#a8c936] bg-[#a8c936]/10 px-2 py-0.5 rounded border border-[#a8c936]/20">
                  {member.primaryPosition}
                </span>
              </div>

              {member.nickname && <p className="text-xs text-slate-400 font-medium">"{member.nickname}"</p>}

              <div className="flex items-center gap-3 mt-2 text-xs text-slate-300 font-mono flex-wrap">
                <span>Foot: {member.preferredFoot}</span>
                <span>•</span>
                <span>Role: {member.role.toUpperCase()}</span>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-2 mt-3">
                {isSelf && onOpenEditProfile && (
                  <button
                    onClick={onOpenEditProfile}
                    className="px-3 py-1 bg-white/10 hover:bg-white/15 text-white text-xs font-semibold rounded-lg transition"
                  >
                    Edit Profile
                  </button>
                )}
                {isOrganizer && onOpenManageRating && (
                  <button
                    onClick={() => onOpenManageRating(member.id)}
                    className="px-3 py-1 bg-[#a8c936] hover:bg-[#b8db3d] text-[#080b0a] text-xs font-semibold rounded-lg transition"
                  >
                    Manage Rating
                  </button>
                )}
                {!isSelf && onSubmitCommunityRating && (
                  <button
                    onClick={() => setIsScoutModalOpen(true)}
                    className="px-3 py-1 bg-white/5 hover:bg-white/10 text-slate-300 hover:text-white text-xs font-semibold rounded-lg border border-white/5 transition flex items-center gap-1"
                  >
                    <Star className="w-3.5 h-3.5 text-amber-400" /> Scout
                  </button>
                )}
              </div>
            </div>

            {/* Large OVR Rating Badge */}
            <div className="hidden sm:flex flex-col items-center justify-center p-3 rounded-2xl bg-[#1c221d] border border-white/10 shrink-0">
              <span className="text-3xl font-extrabold text-white font-mono leading-none">{member.footballCard.overall}</span>
              <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest mt-1">OVR</span>
            </div>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="flex gap-1 border-b border-white/5 pb-2 mb-4 overflow-x-auto">
          {[
            { id: 'overview', label: 'Overview' },
            { id: 'stats', label: 'Career Stats' },
            { id: 'matches', label: 'Matches' },
            { id: 'card', label: 'Player Card' },
            { id: 'achievements', label: 'Achievements' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`px-3.5 py-1.5 rounded-xl text-xs font-semibold whitespace-nowrap transition ${
                activeTab === tab.id
                  ? 'bg-[#1c221d] text-[#a8c936] border border-white/5'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* TAB 1: OVERVIEW */}
        {activeTab === 'overview' && (
          <div className="space-y-4">
            {/* Quick Key Stats Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
              <div className="bg-[#171c19] p-3 rounded-xl border border-white/5">
                <span className="text-[11px] text-slate-400 font-medium block">Matches</span>
                <span className="text-lg font-bold text-white font-mono">{member.stats.matchesPlayed}</span>
              </div>
              <div className="bg-[#171c19] p-3 rounded-xl border border-white/5">
                <span className="text-[11px] text-slate-400 font-medium block">Goals</span>
                <span className="text-lg font-bold text-white font-mono">{member.stats.goals}</span>
              </div>
              <div className="bg-[#171c19] p-3 rounded-xl border border-white/5">
                <span className="text-[11px] text-slate-400 font-medium block">Assists</span>
                <span className="text-lg font-bold text-white font-mono">{member.stats.assists}</span>
              </div>
              <div className="bg-[#171c19] p-3 rounded-xl border border-white/5">
                <span className="text-[11px] text-slate-400 font-medium block">Avg Rating</span>
                <span className="text-lg font-bold text-[#a8c936] font-mono">{member.stats.averageRating.toFixed(1)}</span>
              </div>
            </div>

            {/* Current Form Widget */}
            <div className="bg-[#171c19] p-4 rounded-xl border border-white/5 space-y-2">
              <div className="flex justify-between items-center text-xs">
                <span className="text-slate-300 font-bold uppercase tracking-wider">Current Form (Recent Matches)</span>
                <span className="text-[#a8c936] font-mono font-bold">Avg {formAvg}</span>
              </div>
              <div className="flex items-center gap-2 pt-1 overflow-x-auto">
                {displayFormItems.map((item, idx) => (
                  <div key={idx} className="flex-1 min-w-[50px] bg-[#1c221d] p-2 rounded-lg border border-white/5 text-center shrink-0">
                    <span className="text-xs font-bold text-white font-mono block">{item.rating.toFixed(1)}</span>
                    <span className={`text-[9px] font-extrabold block mt-0.5 ${
                      item.outcome === 'W' ? 'text-emerald-400' : item.outcome === 'L' ? 'text-red-400' : 'text-amber-400'
                    }`}>
                      {item.outcome}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Position Ratings Breakdown */}
            <div className="bg-[#171c19] p-4 rounded-xl border border-white/5 space-y-3">
              <h4 className="text-xs font-bold text-slate-300 uppercase tracking-wider">Position Versatility</h4>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {positionRatings.map((pr) => (
                  <div key={pr.position} className="bg-[#1c221d] p-2.5 rounded-lg border border-white/5 flex justify-between items-center text-xs">
                    <span className="text-slate-400 font-medium">{pr.position}</span>
                    <span className="font-bold text-white font-mono">{pr.ovr} OVR</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* TAB 2: CAREER STATS */}
        {activeTab === 'stats' && (
          <div className="space-y-4">
            {/* Season Filter Selector */}
            <div className="flex items-center justify-between bg-[#171c19] p-3 rounded-xl border border-white/5">
              <span className="text-xs font-semibold text-slate-300">Filter Season</span>
              <select
                value={selectedSeason}
                onChange={(e) => setSelectedSeason(e.target.value)}
                className="bg-[#080b0a] text-white border border-white/10 text-xs font-bold rounded-lg px-3 py-1.5 focus:outline-none"
              >
                <option value="all_time">All Time</option>
                <option value="2026">2026 Season</option>
                <option value="2025">2025 Season</option>
              </select>
            </div>

            {/* Attack & Performance Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="bg-[#171c19] p-4 rounded-xl border border-white/5 space-y-2.5">
                <h4 className="text-xs font-bold text-slate-300 uppercase tracking-wider border-b border-white/5 pb-1">
                  Attack
                </h4>
                <div className="space-y-1.5 text-xs">
                  <div className="flex justify-between"><span className="text-slate-400">Goals</span><span className="font-bold font-mono text-white">{member.stats.goals}</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">Assists</span><span className="font-bold font-mono text-white">{member.stats.assists}</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">Goal Contributions</span><span className="font-bold font-mono text-[#a8c936]">{member.stats.goals + member.stats.assists}</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">Goals / Match</span><span className="font-bold font-mono text-white">{(member.stats.goals / (member.stats.matchesPlayed || 1)).toFixed(2)}</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">Hat-Tricks</span><span className="font-bold font-mono text-white">{member.stats.hatTricks || 0}</span></div>
                </div>
              </div>

              <div className="bg-[#171c19] p-4 rounded-xl border border-white/5 space-y-2.5">
                <h4 className="text-xs font-bold text-slate-300 uppercase tracking-wider border-b border-white/5 pb-1">
                  Performance & Team
                </h4>
                <div className="space-y-1.5 text-xs">
                  <div className="flex justify-between"><span className="text-slate-400">Wins / Draws / Losses</span><span className="font-bold font-mono text-white">{member.stats.wins}W - {member.stats.draws}D - {member.stats.losses}L</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">Win Rate</span><span className="font-bold font-mono text-white">{member.stats.winPct}%</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">MOTM Awards</span><span className="font-bold font-mono text-amber-400">{member.stats.motmCount}</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">Clean Sheets</span><span className="font-bold font-mono text-white">{member.stats.cleanSheets}</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">Attendance Rate</span><span className="font-bold font-mono text-white">{member.stats.attendancePct}%</span></div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* TAB 3: MATCHES */}
        {activeTab === 'matches' && (
          <div className="space-y-3">
            <span className="text-xs font-semibold text-slate-400 block">Recent Match Logs ({playerMatches.length})</span>
            {playerMatches.length === 0 ? (
              <div className="text-center py-8 text-xs text-slate-400 bg-[#171c19] rounded-xl border border-white/5">
                No recent match performance logs recorded yet.
              </div>
            ) : (
              playerMatches.slice(0, 10).map((m) => {
                const pStat = m.result?.playerStats?.find((s) => s.memberProfileId === member.id);
                return (
                  <div key={m.id} className="bg-[#171c19] p-3.5 rounded-xl border border-white/5 flex items-center justify-between text-xs">
                    <div>
                      <span className="font-bold text-white block">{m.title}</span>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-[11px] text-slate-400 font-mono">{m.date} • {m.venueName}</span>
                        {pStat && (pStat.goals > 0 || pStat.assists > 0) && (
                          <span className="text-[10px] text-[#a8c936] font-mono font-bold bg-[#a8c936]/10 px-1.5 py-0.2 rounded border border-[#a8c936]/20">
                            {pStat.goals > 0 && `⚽ ${pStat.goals} `}{pStat.assists > 0 && `🎯 ${pStat.assists}`}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-mono font-bold text-sm text-[#a8c936]">
                        {m.result?.teamAScore} - {m.result?.teamBScore}
                      </span>
                      <span className="bg-[#1c221d] px-2.5 py-1 rounded-lg border border-white/10 font-mono font-extrabold text-white">
                        {pStat?.rating ? pStat.rating.toFixed(1) : '6.5'}
                      </span>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* TAB 4: PLAYER CARD */}
        {activeTab === 'card' && (
          <div className="space-y-4">
            <FootballCard
              name={member.displayName}
              nickname={member.nickname}
              position={member.primaryPosition}
              avatar={member.avatar || 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=200'}
              card={member.footballCard}
              jerseyNumber={member.jerseyNumber}
              preferredFoot={member.preferredFoot}
              cosmeticTheme={member.cardCosmetic || 'default'}
              onShare={() => alert(`Sharing card for ${member.displayName}!`)}
            />

            {/* Cosmetic Theme Selector */}
            <div className="bg-[#171c19] p-4 rounded-xl border border-white/5 space-y-3">
              <h4 className="text-xs font-bold text-slate-300 uppercase tracking-wider">Card Theme & Cosmetics</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {COSMETIC_OPTIONS.map((opt) => (
                  <button
                    key={opt.theme}
                    onClick={() => onUpdateCosmeticTheme && onUpdateCosmeticTheme(member.id, opt.theme)}
                    className={`p-2.5 rounded-xl border text-left transition ${
                      (member.cardCosmetic || 'default') === opt.theme
                        ? 'bg-[#1c221d] border-[#a8c936]/40 text-white'
                        : 'bg-[#080b0a] border-white/5 text-slate-400 hover:text-slate-200'
                    }`}
                  >
                    <span className="font-bold text-xs block">{opt.label}</span>
                    <span className="text-[10px] text-slate-400 block mt-0.5">{opt.desc}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* TAB 5: ACHIEVEMENTS */}
        {activeTab === 'achievements' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {ALL_ACHIEVEMENTS.map((ach) => {
              const isUnlocked = unlockedCodes.includes(ach.code);
              return (
                <div
                  key={ach.id}
                  className={`p-3.5 rounded-xl border flex items-center gap-3 transition ${
                    isUnlocked
                      ? 'bg-[#171c19] border-[#a8c936]/30 text-white'
                      : 'bg-[#080b0a] border-white/5 opacity-50 text-slate-500'
                  }`}
                >
                  <div className="text-2xl p-2 bg-[#1c221d] rounded-xl border border-white/5 shrink-0">
                    {ach.icon}
                  </div>
                  <div>
                    <h5 className="font-bold text-xs text-white">{ach.title}</h5>
                    <p className="text-[11px] text-slate-400 leading-tight mt-0.5">{ach.description}</p>
                    <span className={`text-[9px] font-bold uppercase mt-1 inline-block ${isUnlocked ? 'text-[#a8c936]' : 'text-slate-500'}`}>
                      {isUnlocked ? 'Unlocked' : 'Locked'}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Peer Scouting Modal */}
      <CommunityScoutingModal
        isOpen={isScoutModalOpen}
        onClose={() => setIsScoutModalOpen(false)}
        targetPlayer={member}
        currentUserId={currentUserId}
        isOrganizer={isOrganizer}
        onSubmitRating={onSubmitCommunityRating || (() => {})}
        onApplyCommunitySuggestion={onApplyCommunitySuggestion}
      />
    </div>
  );
};
